import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, KeyRound } from "lucide-react";

const ALL_ROLES = ["admin", "sales", "dispatcher", "driver", "operator", "accounts", "customer"];

const RoleBadge = ({ role }) => (
  <span className="rounded border border-border bg-background px-2 py-0.5 text-xs font-semibold uppercase tracking-widest text-primary">
    {role}
  </span>
);

export default function Users() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const isAccounts = currentUser?.role === "accounts";
  // Accounts users cannot see or create admins.
  const assignableRoles = isAccounts ? ALL_ROLES.filter((r) => r !== "admin") : ALL_ROLES;

  const { data } = useQuery({ queryKey: ["users"], queryFn: () => api.get("/users").then((r) => r.data) });

  // ─── Create ──────────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", phone: "", password: "", role: "sales" });
  const setC = (k) => (e) => setCreateForm({ ...createForm, [k]: e.target.value });
  const create = useMutation({
    mutationFn: (payload) => api.post("/users", payload),
    onSuccess: () => {
      toast.success("User created");
      setCreateOpen(false);
      setCreateForm({ name: "", email: "", phone: "", password: "", role: "sales" });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to create user"),
  });

  // ─── Edit ────────────────────────────────────────────────────────
  const [editUser, setEditUser] = useState(null); // { id, name, email, phone, role, is_active, password? }
  const setE = (k) => (e) => setEditUser({ ...editUser, [k]: e.target.value });
  const update = useMutation({
    mutationFn: ({ id, updates }) => api.patch(`/users/${id}`, updates),
    onSuccess: () => {
      toast.success("User updated");
      setEditUser(null);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to update user"),
  });

  const saveEdit = () => {
    if (!editUser) return;
    const updates = {
      name: editUser.name,
      email: editUser.email,
      phone: editUser.phone,
      role: editUser.role,
      is_active: !!editUser.is_active,
    };
    if (editUser.password) updates.password = editUser.password;
    update.mutate({ id: editUser.id, updates });
  };

  // ─── Delete (confirm) ────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState(null); // full user object
  const del = useMutation({
    mutationFn: (id) => api.delete(`/users/${id}`),
    onSuccess: () => {
      toast.success("User deleted");
      setDeleteTarget(null);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => {
      toast.error(e.response?.data?.detail || "Failed to delete user");
      setDeleteTarget(null);
    },
  });

  // ─── Toggle active (inline) ──────────────────────────────────────
  const toggleActive = (u) =>
    update.mutate({ id: u.id, updates: { is_active: !u.is_active } });

  // Row-level permission — accounts cannot touch admins.
  const canManage = (u) => !(isAccounts && u.role === "admin");

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Users &amp; Access</h1>
          <p className="text-sm text-muted-foreground">
            Role-based access control{isAccounts ? " · you cannot manage admin accounts" : ""}
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-user"><Plus size={14} className="mr-1" /> New user</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={createForm.name} onChange={setC("name")} data-testid="new-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={createForm.email} onChange={setC("email")} data-testid="new-user-email" /></div>
              <div><Label>Phone</Label><Input value={createForm.phone} onChange={setC("phone")} data-testid="new-user-phone" /></div>
              <div><Label>Password</Label><Input type="password" value={createForm.password} onChange={setC("password")} data-testid="new-user-password" /></div>
              <div>
                <Label>Role</Label>
                <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{assignableRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={() => create.mutate(createForm)}
                disabled={!createForm.email || !createForm.password || !createForm.name || create.isPending}
                data-testid="btn-save-user"
              >
                {create.isPending ? "Creating…" : "Create user"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((u) => {
                const manageable = canManage(u);
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="border-b border-border/40 text-sm" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3 text-xs">{u.phone}</td>
                    <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={!!u.is_active}
                        onCheckedChange={() => toggleActive(u)}
                        disabled={!manageable || isSelf || update.isPending}
                        data-testid={`toggle-active-${u.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditUser({ ...u, password: "" })}
                          disabled={!manageable}
                          title={manageable ? "Edit user" : "Admin accounts can only be edited by another admin"}
                          data-testid={`btn-edit-${u.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTarget(u)}
                          disabled={!manageable || isSelf}
                          title={isSelf ? "You cannot delete yourself" : (manageable ? "Delete user" : "Admin accounts can only be deleted by another admin")}
                          data-testid={`btn-delete-${u.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ─── Edit dialog ─── */}
      <Dialog open={!!editUser} onOpenChange={(v) => !v && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={editUser.name || ""} onChange={setE("name")} data-testid="edit-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={editUser.email || ""} onChange={setE("email")} data-testid="edit-user-email" /></div>
              <div><Label>Phone</Label><Input value={editUser.phone || ""} onChange={setE("phone")} data-testid="edit-user-phone" /></div>
              <div>
                <Label className="flex items-center gap-1"><KeyRound size={12} /> Reset password <span className="text-xs font-normal text-muted-foreground">(optional — leave blank to keep current)</span></Label>
                <Input type="password" value={editUser.password || ""} onChange={setE("password")} placeholder="New password" data-testid="edit-user-password" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{assignableRoles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-md border border-border/60 bg-muted/30 px-3 py-2">
                <Label htmlFor="edit-active" className="!m-0">Active</Label>
                <Switch
                  id="edit-active"
                  checked={!!editUser.is_active}
                  onCheckedChange={(v) => setEditUser({ ...editUser, is_active: !!v })}
                  data-testid="edit-user-active"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={update.isPending || !editUser?.name || !editUser?.email} data-testid="btn-save-edit-user">
              {update.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete confirm ─── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{deleteTarget?.name}</span> (<span className="font-mono">{deleteTarget?.email}</span>) will be permanently removed.
              Their access is revoked immediately. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => del.mutate(deleteTarget.id)}
              disabled={del.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="btn-confirm-delete-user"
            >
              {del.isPending ? "Deleting…" : "Delete user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
